import { ChevronDown, Info, ImagePlus, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageLoadingSkeleton } from '../components/PageLoadingSkeleton';
import { getUserFacingError } from '../lib/errors';

const pendingListingDraftKey = 'AgenticHire.pendingListingDraft';

function createId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function createBuyerInput(group = 'required') {
  return {
    id: createId(`buyer-${group}`),
    group,
    name: '',
    type: group === 'required' ? 'text' : 'file_or_text',
    acceptedFormats: '',
    description: '',
  };
}

function createDeliverable() {
  return {
    id: createId('deliverable'),
    name: '',
    format: '',
    deliveryMethod: '',
    description: '',
  };
}

const deliverableFormatOptions = [
  { value: 'spreadsheet', label: 'Spreadsheet' },
  { value: 'document', label: 'Document' },
  { value: 'pdf', label: 'PDF' },
  { value: 'csv', label: 'CSV' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'dataset', label: 'Dataset' },
  { value: 'slide_deck', label: 'Slide deck' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'code', label: 'Code' },
  { value: 'json', label: 'JSON' },
  { value: 'image', label: 'Image' },
  { value: 'link', label: 'Link' },
  { value: 'other', label: 'Other' },
];

const deliverableMethodOptions = [
  { value: 'seller_managed', label: 'Seller managed' },
  { value: 'shared_link', label: 'Shared link' },
  { value: 'direct_download', label: 'Direct download' },
  { value: 'email_delivery', label: 'Email delivery' },
  { value: 'integration_delivery', label: 'Integration delivery' },
];

const flatPricingLabelOptions = [
  'One-time payment',
  'One-time project',
  'Fixed-price project',
  'Setup fee',
  'Pilot project',
];

const subscriptionPricingLabelOptions = [
  'Monthly managed service',
  'Weekly subscription',
  'Quarterly subscription',
  'Annual subscription',
  'Monthly retainer',
];

function getPricingLabelOptions(pricingType) {
  return pricingType === 'subscription' ? subscriptionPricingLabelOptions : flatPricingLabelOptions;
}

function createFaq() {
  return {
    id: createId('faq'),
    question: '',
    answer: '',
  };
}

const initialForm = {
  title: '',
  categoryId: '',
  tagline: '',
  summary: '',
  description: '',
  visibility: 'public',
  coverMode: 'tone',
  tone: 'blue',
  coverFile: null,
  coverPreview: '',
  pricingType: 'flat_price',
  pricingCurrency: 'USD',
  pricingAmount: '',
  pricingLabel: '',
  pricingDescription: '',
  billingInterval: 'month',
  requiresBuyerInputs: '',
  includedAccounts: '',
  includedReports: '',
  includedRuns: '',
  overageEnabled: false,
  overageUnit: '',
  overageAmountPerUnit: '',
  deliveryDays: '',
  revisionCount: '',
  responseHours: '',
  instantHire: true,
  agentType: 'ai_with_human_review',
  automationLevel: 'semi_autonomous',
  stack: '',
  toolsUsed: '',
  integrations: '',
  industries: '',
  bestFor: '',
  notFor: '',
  useCases: '',
  outcomes: '',
  featuredQuote: '',
  outputFormats: '',
  sampleOutputEnabled: false,
  sampleOutputUrl: '',
  sampleOutputDescription: '',
  storesBuyerData: true,
  retentionDays: '',
  usesBuyerDataForTraining: false,
  requiresCredentials: false,
  sensitiveDataAllowed: false,
  thirdPartyToolsUsed: true,
  requiresBuyerReview: true,
  prohibitedUses: '',
  limitations: '',
  demoVideoUrl: '',
  workflowSteps: [''],
  requiredBuyerInputs: [
    {
      id: createId('buyer-required'),
      group: 'required',
      name: '',
      type: 'text',
      acceptedFormats: '',
      description: '',
    },
  ],
  optionalBuyerInputs: [
    {
      id: createId('buyer-optional'),
      group: 'optional',
      name: '',
      type: 'file_or_text',
      acceptedFormats: '',
      description: '',
    },
  ],
  deliverables: [
    {
      id: createId('deliverable'),
      name: '',
      format: '',
      deliveryMethod: '',
      description: '',
    },
  ],
  faqs: [
    {
      id: createId('faq'),
      question: '',
      answer: '',
    },
  ],
};

function parseCsv(value) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseLines(value) {
  return value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);
}

function toNullableNumber(value) {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const next = Number(value);
  return Number.isNaN(next) ? null : next;
}

function toFormNumber(value) {
  return value === null || value === undefined ? '' : String(value);
}

function joinCsv(items) {
  return (items || []).join(', ');
}

function joinLines(items) {
  return (items || []).join('\n');
}

function normalizeComparableLabel(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

function normalizePricingLabel(value, pricingType) {
  const options = getPricingLabelOptions(pricingType);
  const normalizedValue = normalizeComparableLabel(value);
  return options.find((option) => normalizeComparableLabel(option) === normalizedValue) || '';
}

function buyerInputToForm(item, group) {
  return {
    id: createId(`buyer-${group}`),
    group,
    name: item.name || '',
    type: item.type || (group === 'required' ? 'text' : 'file_or_text'),
    acceptedFormats: joinCsv(item.acceptedFormats),
    description: item.description || '',
  };
}

function deliverableToForm(item) {
  return {
    id: createId('deliverable'),
    name: item.name || '',
    format: item.format || '',
    deliveryMethod: item.deliveryMethod || '',
    description: item.description || '',
  };
}

function faqToForm(faq) {
  return {
    id: createId('faq'),
    question: faq.question || '',
    answer: faq.answer || '',
  };
}

function listingToForm(listing) {
  const requiredBuyerInputs = (listing.buyerInputs?.required || []).map((item) =>
    buyerInputToForm(item, 'required')
  );
  const optionalBuyerInputs = (listing.buyerInputs?.optional || []).map((item) =>
    buyerInputToForm(item, 'optional')
  );
  const deliverables = (listing.deliverables || []).map(deliverableToForm);
  const faqs = (listing.faqs || []).map(faqToForm);
  const workflowSteps = listing.workflow?.length ? listing.workflow : [''];
  const hasBuyerInputs = requiredBuyerInputs.length > 0 || optionalBuyerInputs.length > 0;
  const includedUsage = listing.includedUsage || listing.packages?.[0]?.includedUsage || {};
  const overage = listing.overage || listing.packages?.[0]?.overage || {};

  return {
    ...initialForm,
    title: listing.title || '',
    categoryId: listing.category?.id || listing.categoryId || '',
    tagline: listing.tagline || '',
    summary: listing.summary || '',
    description: listing.description || '',
    visibility: listing.visibility || 'public',
    coverMode: listing.thumbnailUrl ? 'upload' : 'tone',
    tone: listing.tone || 'blue',
    coverFile: null,
    coverPreview: listing.thumbnailUrl || '',
    pricingType: listing.pricingType || 'flat_price',
    pricingCurrency: listing.pricingCurrency || 'USD',
    pricingAmount: toFormNumber(listing.pricingAmount ?? listing.priceFrom),
    pricingLabel: normalizePricingLabel(listing.pricingLabel, listing.pricingType),
    pricingDescription: listing.pricingDescription || '',
    billingInterval: listing.billingInterval || 'month',
    requiresBuyerInputs: hasBuyerInputs ? 'yes' : 'no',
    includedAccounts: toFormNumber(includedUsage.accounts),
    includedReports: toFormNumber(includedUsage.reports),
    includedRuns: toFormNumber(includedUsage.runs),
    overageEnabled: Boolean(overage.enabled),
    overageUnit: overage.unit || '',
    overageAmountPerUnit: toFormNumber(overage.amountPerUnit),
    deliveryDays: toFormNumber(listing.deliveryDays),
    revisionCount: toFormNumber(listing.revisionCount),
    responseHours: toFormNumber(listing.responseHours),
    instantHire: listing.instantHire ?? true,
    agentType: listing.agentType || 'ai_with_human_review',
    automationLevel: listing.automationLevel || 'semi_autonomous',
    stack: joinCsv(listing.stack),
    toolsUsed: joinCsv(listing.toolsUsed),
    integrations: joinCsv(listing.integrations),
    industries: joinCsv(listing.industries),
    bestFor: joinCsv(listing.bestFor),
    notFor: joinCsv(listing.notFor),
    useCases: joinCsv(listing.useCases),
    outcomes: joinCsv(listing.outcomes),
    featuredQuote: listing.featuredQuote || '',
    outputFormats: joinCsv(listing.outputFormats),
    sampleOutputEnabled: Boolean(listing.sampleOutput?.enabled),
    sampleOutputUrl: listing.sampleOutput?.url || '',
    sampleOutputDescription: listing.sampleOutput?.description || '',
    storesBuyerData: listing.dataPolicy?.storesBuyerData ?? true,
    retentionDays: toFormNumber(listing.dataPolicy?.retentionDays),
    usesBuyerDataForTraining: Boolean(listing.dataPolicy?.usesBuyerDataForTraining),
    requiresCredentials: Boolean(listing.dataPolicy?.requiresCredentials),
    sensitiveDataAllowed: Boolean(listing.dataPolicy?.sensitiveDataAllowed),
    thirdPartyToolsUsed: listing.dataPolicy?.thirdPartyToolsUsed ?? true,
    requiresBuyerReview: listing.requiresBuyerReview ?? true,
    prohibitedUses: joinCsv(listing.prohibitedUses),
    limitations: joinLines(listing.limitations),
    demoVideoUrl: listing.demoVideoUrl || '',
    workflowSteps,
    requiredBuyerInputs: requiredBuyerInputs.length ? requiredBuyerInputs : [createBuyerInput('required')],
    optionalBuyerInputs: optionalBuyerInputs.length ? optionalBuyerInputs : [createBuyerInput('optional')],
    deliverables: deliverables.length ? deliverables : [createDeliverable()],
    faqs: faqs.length ? faqs : [createFaq()],
  };
}

function readPendingListingDraft() {
  try {
    const raw = window.localStorage.getItem(pendingListingDraftKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePendingListingDraft(payload) {
  window.localStorage.setItem(
    pendingListingDraftKey,
    JSON.stringify({
      payload,
      savedAt: new Date().toISOString(),
    })
  );
}

function clearPendingListingDraft() {
  window.localStorage.removeItem(pendingListingDraftKey);
}

function buildAutoSlug(title) {
  const base = title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);

  const fallback = base || 'agent';
  return `${fallback}-${Math.random().toString(36).slice(2, 8)}`;
}

function FieldLabel({ label, help, required = false }) {
  return (
    <span className="form-label form-label-with-help">
      <span className="form-label-text">
        <span>{label}</span>
        {required ? <span className="field-required-badge">Required</span> : null}
      </span>
      {help ? (
        <span className="field-help" tabIndex={0}>
          <Info size={14} />
          <span className="field-tooltip">{help}</span>
        </span>
      ) : null}
    </span>
  );
}

function AccordionSection({ title, description, children, defaultOpen = false }) {
  return (
    <details className="studio-accordion" open={defaultOpen}>
      <summary className="studio-accordion-summary">
        <div className="studio-accordion-copy">
          <h2>{title}</h2>
          {description ? <p>{description}</p> : null}
        </div>
        <ChevronDown size={18} />
      </summary>
      <div className="studio-accordion-body">{children}</div>
    </details>
  );
}

export function StudioPage({
  snapshot,
  loading,
  authLoading,
  currentUser,
  onLoadProfile,
  onCreateListing,
  onUpdateListing,
  onCreateStripeConnectLink,
}) {
  const navigate = useNavigate();
  const { listingId } = useParams();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('');
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [stripeStatus, setStripeStatus] = useState('');
  const [guestDraftModalOpen, setGuestDraftModalOpen] = useState(false);
  const [pendingDraftSaving, setPendingDraftSaving] = useState(false);
  const [listingSubmitting, setListingSubmitting] = useState(false);
  const [hydratedListingId, setHydratedListingId] = useState(null);

  const editingListing = useMemo(
    () => snapshot?.listings.find((listing) => listing.id === listingId) || null,
    [listingId, snapshot]
  );
  const isEditing = Boolean(listingId);
  const canEditListing = Boolean(editingListing && currentUser?.id === editingListing.seller?.id);
  const stripeAccountConnected = Boolean(profile?.stripeAccountId);
  const stripePayoutsEnabled = Boolean(profile?.stripePayoutsEnabled);
  const shouldShowStripeGate = Boolean(currentUser && !isEditing && !stripeAccountConnected);
  const publishButtonLabel = currentUser
    ? isEditing
      ? 'Save listing'
      : stripePayoutsEnabled
      ? 'Publish agent'
      : 'Submit agent for review'
    : 'Submit agent';
  const listingSubmitLoadingLabel = isEditing
    ? 'Saving listing'
    : stripePayoutsEnabled
      ? 'Publishing agent'
      : 'Saving listing';

  const checklist = useMemo(
    () => ({
      required: [
        {
          id: 'identity',
          label: 'Add a title and category.',
          complete: Boolean(form.title.trim() && form.categoryId),
        },
        {
          id: 'positioning',
          label: 'Write a tagline, summary, and description.',
          complete: Boolean(form.tagline.trim() && form.summary.trim() && form.description.trim()),
        },
        {
          id: 'pricing',
          label: 'Set pricing and delivery details.',
          complete: Boolean(
            form.pricingType &&
              form.pricingAmount.trim() &&
              form.deliveryDays.trim() &&
              form.revisionCount.trim() &&
              form.responseHours.trim()
          ),
        },
        {
          id: 'buyer-inputs',
          label: 'Answer whether the buyer needs to provide inputs.',
          complete:
            form.requiresBuyerInputs === 'no' ||
            (form.requiresBuyerInputs === 'yes' &&
              form.requiredBuyerInputs.some(
                (item) => item.name.trim() && item.description.trim()
              )),
        },
        {
          id: 'deliverables',
          label: 'List at least one deliverable and your workflow steps.',
          complete: Boolean(
            form.deliverables.some(
              (item) =>
                item.name.trim() &&
                item.format &&
                item.deliveryMethod &&
                item.description.trim()
            ) && form.workflowSteps.some((step) => step.trim())
          ),
        },
      ],
      optional: [
        {
          id: 'cover',
          label: 'Customize the cover image or color.',
          complete: Boolean(
            (form.coverMode === 'upload' && form.coverFile) || form.tone !== initialForm.tone
          ),
        },
        {
          id: 'agent',
          label: 'Add agent details, tools, and integrations.',
          complete: Boolean(form.toolsUsed.trim() && form.integrations.trim() && form.stack.trim()),
        },
        {
          id: 'proof',
          label: 'Add proof, trust details, or FAQs.',
          complete: Boolean(
            form.featuredQuote.trim() ||
              form.sampleOutputEnabled ||
              form.faqs.some((faq) => faq.question.trim() && faq.answer.trim())
          ),
        },
      ],
    }),
    [
      form.categoryId,
      form.coverFile,
      form.deliveryDays,
      form.deliverables,
      form.description,
      form.faqs,
      form.integrations,
      form.requiresBuyerInputs,
      form.pricingType,
      form.pricingAmount,
      form.requiredBuyerInputs,
      form.responseHours,
      form.revisionCount,
      form.sampleOutputEnabled,
      form.stack,
      form.summary,
      form.tagline,
      form.title,
      form.tone,
      form.toolsUsed,
      form.workflowSteps,
      form.featuredQuote,
      form.coverMode,
    ]
  );

  useEffect(() => {
    let active = true;

    async function loadProfile() {
      if (!currentUser || !onLoadProfile) {
        setProfile(null);
        return;
      }

      setProfileLoading(true);
      setStripeStatus('');

      try {
        const result = await onLoadProfile(currentUser);
        if (active) {
          setProfile(result?.profile || null);
        }
      } catch (error) {
        if (active) {
          setStripeStatus(getUserFacingError(error, 'Unable to load your seller profile right now.'));
        }
      } finally {
        if (active) {
          setProfileLoading(false);
        }
      }
    }

    loadProfile();

    return () => {
      active = false;
    };
  }, [currentUser, onLoadProfile]);

  useEffect(() => {
    if (!isEditing) {
      if (hydratedListingId) {
        setForm(initialForm);
        setHydratedListingId(null);
      }
      return;
    }

    if (!editingListing || !canEditListing || hydratedListingId === editingListing.id) {
      return;
    }

    setForm(listingToForm(editingListing));
    setHydratedListingId(editingListing.id);
    setStatus('');
  }, [canEditListing, editingListing, hydratedListingId, isEditing]);

  useEffect(() => {
    let active = true;

    async function savePendingDraft() {
      if (isEditing || !currentUser || !stripeAccountConnected || !onCreateListing || pendingDraftSaving) {
        return;
      }

      const pendingDraft = readPendingListingDraft();
      if (!pendingDraft?.payload) {
        return;
      }

      setPendingDraftSaving(true);
      setStatus('Saving your submitted agent draft...');

      try {
        const result = await onCreateListing({
          ...pendingDraft.payload,
          status: stripePayoutsEnabled ? 'published' : 'draft',
        });

        clearPendingListingDraft();
        if (active) {
          setStatus(
            result.mock
              ? 'Saved in sample mode.'
              : result.listing?.status === 'published'
                ? 'Agent published.'
                : 'Agent saved as a draft. It will publish automatically after Stripe approves payouts.'
          );
          setForm(initialForm);
        }
      } catch (error) {
        if (active) {
          setStatus(getUserFacingError(error, 'Unable to save that draft right now. Please try again.'));
        }
      } finally {
        if (active) {
          setPendingDraftSaving(false);
        }
      }
    }

    savePendingDraft();

    return () => {
      active = false;
    };
  }, [
    currentUser,
    isEditing,
    onCreateListing,
    pendingDraftSaving,
    stripeAccountConnected,
    stripePayoutsEnabled,
  ]);

  if (authLoading || loading || !snapshot || profileLoading) {
    return <PageLoadingSkeleton label="Loading seller studio" />;
  }

  if (isEditing && !editingListing) {
    return (
      <section className="page-shell">
        <div className="empty-state">
          <h1>Listing not found</h1>
          <p>This listing does not exist or is not available to your account.</p>
        </div>
      </section>
    );
  }

  if (isEditing && !canEditListing) {
    return (
      <section className="page-shell">
        <div className="empty-state">
          <h1>You cannot edit this listing</h1>
          <p>Only the seller who owns this agent listing can edit its details.</p>
        </div>
      </section>
    );
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function updateArrayItem(field, id, nextPatch) {
    setForm((current) => ({
      ...current,
      [field]: current[field].map((item) => (item.id === id ? { ...item, ...nextPatch } : item)),
    }));
  }

  function removeArrayItem(field, id) {
    setForm((current) => ({
      ...current,
      [field]: current[field].filter((item) => item.id !== id),
    }));
  }

  function addArrayItem(field, item) {
    setForm((current) => ({
      ...current,
      [field]: [...current[field], item],
    }));
  }

  function updateWorkflowStep(index, value) {
    setForm((current) => ({
      ...current,
      workflowSteps: current.workflowSteps.map((step, stepIndex) => (stepIndex === index ? value : step)),
    }));
  }

  function addWorkflowStep() {
    setForm((current) => ({
      ...current,
      workflowSteps: [...current.workflowSteps, ''],
    }));
  }

  function removeWorkflowStep(index) {
    setForm((current) => ({
      ...current,
      workflowSteps: current.workflowSteps.filter((_, stepIndex) => stepIndex !== index),
    }));
  }

  function handleCoverFileChange(event) {
    const file = event.target.files?.[0] || null;

    if (!file) {
      updateField('coverFile', null);
      updateField('coverPreview', '');
      return;
    }

    updateField('coverFile', file);
    updateField('coverPreview', URL.createObjectURL(file));
    updateField('coverMode', 'upload');
  }

  function handlePricingTypeChange(nextType) {
    setForm((current) => ({
      ...current,
      pricingType: nextType,
      pricingLabel: normalizePricingLabel(current.pricingLabel, nextType),
      billingInterval: nextType === 'subscription' ? current.billingInterval || 'month' : 'month',
    }));
  }

  function buildListingPayload({ includeFiles = true } = {}) {
    return {
      title: form.title.trim(),
      slug: editingListing?.slug || buildAutoSlug(form.title),
      categoryId: form.categoryId,
      tagline: form.tagline.trim(),
      summary: form.summary.trim(),
      description: form.description.trim(),
      status: stripePayoutsEnabled ? 'published' : 'draft',
      visibility: form.visibility,
      pricing: {
        type: form.pricingType,
        currency: form.pricingCurrency,
        amount: Number(form.pricingAmount),
        label: form.pricingLabel.trim() || null,
        description: form.pricingDescription.trim() || null,
        billingInterval: form.pricingType === 'subscription' ? form.billingInterval : null,
        includedUsage: {
          accounts: toNullableNumber(form.includedAccounts),
          reports: toNullableNumber(form.includedReports),
          runs: toNullableNumber(form.includedRuns),
        },
        overage: {
          enabled: form.overageEnabled,
          unit: form.overageEnabled ? form.overageUnit.trim() || null : null,
          amountPerUnit: form.overageEnabled ? toNullableNumber(form.overageAmountPerUnit) : null,
        },
      },
      delivery: {
        deliveryDays: Number(form.deliveryDays),
        revisionCount: Number(form.revisionCount),
        responseHours: Number(form.responseHours),
        instantHire: form.instantHire,
      },
      agent: {
        agentType: form.agentType,
        automationLevel: form.automationLevel,
        stack: parseCsv(form.stack),
        toolsUsed: parseCsv(form.toolsUsed),
        integrations: parseCsv(form.integrations),
      },
      market: {
        industries: parseCsv(form.industries),
        bestFor: parseCsv(form.bestFor),
        notFor: parseCsv(form.notFor),
        useCases: parseCsv(form.useCases),
        outcomes: parseCsv(form.outcomes),
      },
      buyerInputs: {
        required:
          form.requiresBuyerInputs === 'yes'
            ? form.requiredBuyerInputs
                .filter((item) => item.name.trim())
                .map((item) => ({
                  name: item.name.trim(),
                  type: item.type,
                  acceptedFormats: parseCsv(item.acceptedFormats),
                  description: item.description.trim(),
                }))
            : [],
        optional:
          form.requiresBuyerInputs === 'yes'
            ? form.optionalBuyerInputs
                .filter((item) => item.name.trim())
                .map((item) => ({
                  name: item.name.trim(),
                  type: item.type,
                  acceptedFormats: parseCsv(item.acceptedFormats),
                  description: item.description.trim(),
                }))
            : [],
      },
      deliverables: form.deliverables
        .filter((item) => item.name.trim())
        .map((item) => ({
          name: item.name.trim(),
          format: item.format,
          deliveryMethod: item.deliveryMethod,
          description: item.description.trim(),
        })),
      outputFormats: parseCsv(form.outputFormats),
      sampleOutput: {
        enabled: form.sampleOutputEnabled,
        url: form.sampleOutputUrl.trim() || null,
        description: form.sampleOutputDescription.trim() || null,
      },
      workflow: form.workflowSteps.filter((step) => step.trim()),
      dataPolicy: {
        storesBuyerData: form.storesBuyerData,
        retentionDays: toNullableNumber(form.retentionDays),
        usesBuyerDataForTraining: form.usesBuyerDataForTraining,
        requiresCredentials: form.requiresCredentials,
        sensitiveDataAllowed: form.sensitiveDataAllowed,
        thirdPartyToolsUsed: form.thirdPartyToolsUsed,
      },
      limitations: parseLines(form.limitations),
      compliance: {
        requiresBuyerReview: form.requiresBuyerReview,
        prohibitedUses: parseCsv(form.prohibitedUses),
      },
      media: {
        coverMode: form.coverMode,
        thumbnailFile: includeFiles && form.coverMode === 'upload' ? form.coverFile : null,
        thumbnailPreview: includeFiles ? form.coverPreview || null : null,
        demoVideoUrl: form.demoVideoUrl.trim() || null,
        galleryUrls: [],
      },
      faqs: form.faqs
        .filter((faq) => faq.question.trim() && faq.answer.trim())
        .map((faq) => ({
          question: faq.question.trim(),
          answer: faq.answer.trim(),
        })),
      featuredQuote: form.featuredQuote.trim() || null,
      tone: form.tone,
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!currentUser) {
      const payload = buildListingPayload({ includeFiles: false });
      writePendingListingDraft(payload);
      setGuestDraftModalOpen(true);
      setStatus('');
      return;
    }

    if (!stripeAccountConnected) {
      setStatus('Connect Stripe before submitting your agent.');
      return;
    }

    setStatus('');
    setListingSubmitting(true);

    const payload = buildListingPayload({ includeFiles: true });

    try {
      const result = isEditing
        ? await onUpdateListing(editingListing.id, payload)
        : await onCreateListing(payload);
      setStatus(
        result.mock
          ? 'Saved in sample mode.'
          : isEditing
            ? 'Listing updated.'
            : result.listing?.status === 'published'
            ? 'Agent published.'
            : 'Agent saved. It will publish automatically after Stripe approves payouts.'
      );
      if (isEditing) {
        navigate(`/agents/${editingListing.slug}`);
      } else {
        setForm(initialForm);
      }
    } catch (error) {
      setStatus(getUserFacingError(error, 'Unable to save that listing right now. Please try again.'));
    } finally {
      setListingSubmitting(false);
    }
  }

  async function handleStripeConnect() {
    const opensDashboard = stripePayoutsEnabled;
    const dashboardWindow = opensDashboard ? window.open('', '_blank', 'noopener,noreferrer') : null;
    setStripeStatus('Opening Stripe setup...');

    try {
      const result = await onCreateStripeConnectLink('/studio');
      if (result?.url) {
        if (result.mode === 'dashboard') {
          if (dashboardWindow) {
            dashboardWindow.location.href = result.url;
          } else {
            window.open(result.url, '_blank', 'noopener,noreferrer');
          }
          setStripeStatus('');
          return;
        }

        dashboardWindow?.close();
        window.location.href = result.url;
        return;
      }
      dashboardWindow?.close();
      setStripeStatus('Unable to open Stripe right now.');
    } catch (error) {
      dashboardWindow?.close();
      setStripeStatus(getUserFacingError(error, 'Unable to open Stripe right now. Please try again.'));
    }
  }

  function handleCreateAccountFromDraft() {
    navigate('/login?mode=signup&redirect=/studio');
  }

  function handleSignInFromDraft() {
    navigate('/login?redirect=/studio');
  }

  return (
    <section className="page-shell studio-page">
      <div className="page-head seller-head">
        <div>
          <span className="eyebrow left">Seller studio</span>
          <h1>
            {isEditing ? `Edit ${editingListing?.title || 'agent listing'}.` : 'Create an agent listing buyers can evaluate quickly.'}
          </h1>
          <p>
            {isEditing
              ? 'Update the offer, pricing model, delivery terms, buyer inputs, and proof in one place.'
              : 'Define the offer, pricing model, delivery terms, buyer inputs, and proof in one place.'}
          </p>
        </div>
      </div>

      {guestDraftModalOpen ? (
        <div className="studio-modal-backdrop" role="presentation">
          <section className="studio-stripe-modal" role="dialog" aria-modal="true" aria-labelledby="guest-draft-title">
            <span className="eyebrow left">Draft saved</span>
            <h2 id="guest-draft-title">Your agent listing has been submitted.</h2>
            <p>
              Create an account to attach this draft to your seller profile. After that, connect Stripe so the
              listing can go live once payouts are approved.
            </p>
            <div className="studio-modal-actions">
              <button type="button" className="primary-button ui-button full-width" onClick={handleCreateAccountFromDraft}>
                Create account
              </button>
              <button type="button" className="ghost-button ui-button full-width" onClick={handleSignInFromDraft}>
                Sign in
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {shouldShowStripeGate ? (
        <div className="studio-modal-backdrop" role="presentation">
          <section className="studio-stripe-modal" role="dialog" aria-modal="true" aria-labelledby="stripe-gate-title">
            <span className="eyebrow left">Stripe required</span>
            <h2 id="stripe-gate-title">Connect payouts before listing an agent.</h2>
            <p>
              AgenticHire collects buyer payments through Stripe and sends seller payouts through your connected account.
            </p>
            <div className="studio-modal-actions">
              <button type="button" className="primary-button ui-button full-width" onClick={handleStripeConnect}>
                Connect Stripe
              </button>
            </div>
            {stripeStatus ? <p className="status-note">{stripeStatus}</p> : null}
          </section>
        </div>
      ) : null}

      {listingSubmitting ? (
        <div className="studio-modal-backdrop" role="presentation">
          <section className="auth-card login-card is-submitting studio-saving-modal" role="status" aria-live="polite">
            <div className="login-loading-state">
              <div className="loader" aria-hidden="true" />
              <span>{listingSubmitLoadingLabel}</span>
            </div>
          </section>
        </div>
      ) : null}

      <div className="studio-grid seller-studio-grid">
        <aside className="detail-panel studio-side-panel">
          <h2>Seller checklist</h2>
          {currentUser ? (
            <div className={`stripe-readiness-card${stripePayoutsEnabled ? ' is-ready' : ''}`}>
              <strong>{stripePayoutsEnabled ? 'Stripe ready' : 'Stripe under review'}</strong>
              <p>
                {stripePayoutsEnabled
                  ? 'New public listings can accept orders immediately.'
                  : 'You can submit the listing now. It stays private until Stripe approves payouts.'}
              </p>
            </div>
          ) : null}
          <div className="seller-checklist-group">
            <strong className="seller-checklist-heading">Required to publish</strong>
            <ul className="seller-checklist">
              {checklist.required.map((item) => (
                <li key={item.id} className={item.complete ? 'is-complete' : ''}>
                  <span className="seller-checklist-bubble" aria-hidden="true" />
                  <span className="seller-checklist-text">{item.label}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="seller-checklist-group">
            <strong className="seller-checklist-heading">Optional improvements</strong>
            <p className="seller-checklist-note">
              These are not required, but they make the listing easier to trust and buy.
            </p>
            <ul className="seller-checklist">
              {checklist.optional.map((item) => (
                <li key={item.id} className={item.complete ? 'is-complete' : ''}>
                  <span className="seller-checklist-bubble" aria-hidden="true" />
                  <span className="seller-checklist-text">{item.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <form className="detail-panel studio-form seller-form ui-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Core listing</h2>
            <div className="form-grid">
              <label className="form-field form-field-wide">
                <FieldLabel
                  label="Agent title"
                  help="Lead with the outcome or job the agent handles."
                  required
                />
                <input
                  className="ui-input"
                  type="text"
                  placeholder="DemandPilot Outbound"
                  value={form.title}
                  onChange={(event) => updateField('title', event.target.value)}
                  required
                />
              </label>
              <label className="form-field">
                <FieldLabel
                  label="Category"
                  help="Choose the service bucket buyers will filter by."
                  required
                />
                <select
                  className="ui-select"
                  value={form.categoryId}
                  onChange={(event) => updateField('categoryId', event.target.value)}
                  required
                >
                  <option value="">Select category</option>
                  {snapshot.categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="form-field">
                <FieldLabel label="Visibility" help="Control whether the listing is public or hidden." />
                <select
                  className="ui-select"
                  value={form.visibility}
                  onChange={(event) => updateField('visibility', event.target.value)}
                >
                  <option value="public">Public</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="private">Private</option>
                </select>
              </label>
            </div>

            <label className="form-field">
              <FieldLabel
                label="Tagline"
                help="A one-line promise that explains what the agent does and for whom."
                required
              />
              <input
                className="ui-input"
                type="text"
                placeholder="Outbound research and personalization agent for lean growth teams."
                value={form.tagline}
                onChange={(event) => updateField('tagline', event.target.value)}
                required
              />
            </label>

            <label className="form-field">
              <FieldLabel label="Short summary" help="Used in cards and condensed previews." required />
              <textarea
                className="ui-textarea"
                rows="3"
                placeholder="Research accounts, score leads, and generate personalized outbound copy."
                value={form.summary}
                onChange={(event) => updateField('summary', event.target.value)}
                required
              />
            </label>

            <label className="form-field">
              <FieldLabel
                label="Full description"
                help="Explain the problem, workflow, target buyer, and what gets delivered."
                required
              />
              <textarea
                className="ui-textarea"
                rows="5"
                placeholder="Describe what the agent does, who it is best for, and how the service works."
                value={form.description}
                onChange={(event) => updateField('description', event.target.value)}
                required
              />
            </label>
          </div>

          <AccordionSection
            title="Cover"
            description="Optional. Add a custom visual or pick a default color cover."
          >
            <div className="form-section optional-form-section">
            <div className="studio-toggle-row">
              <button
                type="button"
                className={`studio-toggle-chip${form.coverMode === 'tone' ? ' active' : ''}`}
                onClick={() => updateField('coverMode', 'tone')}
              >
                Default color cover
              </button>
              <button
                type="button"
                className={`studio-toggle-chip${form.coverMode === 'upload' ? ' active' : ''}`}
                onClick={() => updateField('coverMode', 'upload')}
              >
                Upload image
              </button>
            </div>

            {form.coverMode === 'upload' ? (
              <div className="cover-upload-panel">
                <label className="cover-upload-dropzone">
                  <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleCoverFileChange} />
                  <ImagePlus size={18} />
                  <span>Upload a cover image</span>
                  <small>PNG, JPG, or WEBP. Recommended wide landscape ratio.</small>
                </label>

                <div className={`cover-preview-card${form.coverPreview ? ' has-image' : ''} tone-${form.tone}`}>
                  {form.coverPreview ? <img src={form.coverPreview} alt="Listing cover preview" /> : null}
                  <span className="listing-badge">Preview</span>
                </div>
              </div>
            ) : (
              <div className="cover-tone-grid">
                {['blue', 'graphite', 'plum', 'burgundy'].map((tone) => (
                  <button
                    key={tone}
                    type="button"
                    className={`cover-tone-option tone-${tone}${form.tone === tone ? ' active' : ''}`}
                    onClick={() => updateField('tone', tone)}
                  >
                    <span className="listing-badge">{tone}</span>
                  </button>
                ))}
              </div>
            )}
            </div>
          </AccordionSection>

          <div className="form-section">
            <h2>Pricing and delivery</h2>
            <div className="form-grid">
              <label className="form-field">
                <FieldLabel
                  label="Pricing model"
                  help="Choose whether buyers pay once or on a recurring basis."
                  required
                />
                <select
                  className="ui-select"
                  value={form.pricingType}
                  onChange={(event) => handlePricingTypeChange(event.target.value)}
                >
                  <option value="flat_price">Flat price</option>
                  <option value="subscription">Subscription</option>
                </select>
              </label>
              <label className="form-field">
                <FieldLabel label="Currency" help="Display currency for the public price." />
                <select
                  className="ui-select"
                  value={form.pricingCurrency}
                  onChange={(event) => updateField('pricingCurrency', event.target.value)}
                >
                  <option value="USD">USD</option>
                </select>
              </label>
              <label className="form-field">
                <FieldLabel
                  label="Base amount"
                  help="The headline price shown on cards and the listing page."
                  required
                />
                <input
                  className="ui-input"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="1800"
                  value={form.pricingAmount}
                  onChange={(event) => updateField('pricingAmount', event.target.value)}
                  required
                />
              </label>
              <label className="form-field">
                <FieldLabel
                  label="Billing interval"
                  help="Flat-price listings are always one-time. Subscription listings can recur weekly, monthly, quarterly, or yearly."
                />
                {form.pricingType === 'subscription' ? (
                  <select
                    className="ui-select"
                    value={form.billingInterval}
                    onChange={(event) => updateField('billingInterval', event.target.value)}
                  >
                    <option value="month">Monthly</option>
                    <option value="quarter">Quarterly</option>
                    <option value="year">Yearly</option>
                    <option value="week">Weekly</option>
                  </select>
                ) : (
                  <input className="ui-input is-readonly" type="text" value="One Time" readOnly />
                )}
              </label>
              <label className="form-field form-field-wide">
                <FieldLabel label="Pricing label" help="Short label such as One-time project or Monthly managed service." />
                <select
                  className="ui-select"
                  value={form.pricingLabel}
                  onChange={(event) => updateField('pricingLabel', event.target.value)}
                >
                  <option value="">Select pricing label</option>
                  {getPricingLabelOptions(form.pricingType).map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="form-field">
              <FieldLabel label="Pricing description" help="Explain what the buyer is paying for at this price point." />
              <textarea
                className="ui-textarea"
                rows="3"
                placeholder="A one-time setup and delivery of the outbound research workflow."
                value={form.pricingDescription}
                onChange={(event) => updateField('pricingDescription', event.target.value)}
              />
            </label>

            <div className="form-grid">
              <label className="form-field">
                <FieldLabel label="Included accounts" help="Optional included usage for account-based services." />
                <input
                  className="ui-input"
                  type="number"
                  min="0"
                  placeholder="100"
                  value={form.includedAccounts}
                  onChange={(event) => updateField('includedAccounts', event.target.value)}
                />
              </label>
              <label className="form-field">
                <FieldLabel label="Included reports" help="Optional included report count." />
                <input
                  className="ui-input"
                  type="number"
                  min="0"
                  placeholder="1"
                  value={form.includedReports}
                  onChange={(event) => updateField('includedReports', event.target.value)}
                />
              </label>
              <label className="form-field">
                <FieldLabel label="Included runs" help="Optional included execution runs." />
                <input
                  className="ui-input"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={form.includedRuns}
                  onChange={(event) => updateField('includedRuns', event.target.value)}
                />
              </label>
              <label className="form-field">
                <FieldLabel label="Overage enabled" help="Turn on overage pricing if usage can exceed the included amount." />
                <select
                  className="ui-select"
                  value={form.overageEnabled ? 'yes' : 'no'}
                  onChange={(event) => updateField('overageEnabled', event.target.value === 'yes')}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </label>
            </div>

            {form.overageEnabled ? (
              <div className="form-grid">
                <label className="form-field">
                  <FieldLabel label="Overage unit" help="For example account, report, seat, or run." />
                  <input
                    className="ui-input"
                    type="text"
                    placeholder="account"
                    value={form.overageUnit}
                    onChange={(event) => updateField('overageUnit', event.target.value)}
                  />
                </label>
                <label className="form-field">
                  <FieldLabel label="Overage price" help="Amount charged per additional unit." />
                  <input
                    className="ui-input"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="15"
                    value={form.overageAmountPerUnit}
                    onChange={(event) => updateField('overageAmountPerUnit', event.target.value)}
                  />
                </label>
              </div>
            ) : null}

            <div className="form-grid">
              <label className="form-field">
                <FieldLabel label="Delivery days" help="How long the first delivery takes." required />
                <input
                  className="ui-input"
                  type="number"
                  min="1"
                  value={form.deliveryDays}
                  onChange={(event) => updateField('deliveryDays', event.target.value)}
                  required
                />
              </label>
              <label className="form-field">
                <FieldLabel label="Revisions" help="How many revision rounds are included." required />
                <input
                  className="ui-input"
                  type="number"
                  min="0"
                  value={form.revisionCount}
                  onChange={(event) => updateField('revisionCount', event.target.value)}
                  required
                />
              </label>
              <label className="form-field">
                <FieldLabel
                  label="Response hours"
                  help="Typical first response time to buyer messages."
                  required
                />
                <input
                  className="ui-input"
                  type="number"
                  min="1"
                  value={form.responseHours}
                  onChange={(event) => updateField('responseHours', event.target.value)}
                  required
                />
              </label>
              <label className="form-field">
                <FieldLabel label="Instant hire" help="If enabled, buyers can treat this as ready-to-buy without extra scoping." />
                <select
                  className="ui-select"
                  value={form.instantHire ? 'yes' : 'no'}
                  onChange={(event) => updateField('instantHire', event.target.value === 'yes')}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
            </div>
          </div>

          <AccordionSection
            title="Agent and market fit"
            description="Optional. Explain how the agent works and who it fits best."
          >
            <div className="form-section optional-form-section">
            <div className="form-grid">
              <label className="form-field">
                <FieldLabel label="Agent type" help="Describe how autonomous the agent is in practice." />
                <select
                  className="ui-select"
                  value={form.agentType}
                  onChange={(event) => updateField('agentType', event.target.value)}
                >
                  <option value="ai_only">AI only</option>
                  <option value="ai_with_human_review">AI with human review</option>
                  <option value="human_with_ai_tools">Human with AI tools</option>
                  <option value="custom">Custom</option>
                </select>
              </label>
              <label className="form-field">
                <FieldLabel label="Automation level" help="Sets buyer expectations around how much is automated." />
                <select
                  className="ui-select"
                  value={form.automationLevel}
                  onChange={(event) => updateField('automationLevel', event.target.value)}
                >
                  <option value="manual_assist">Manual assist</option>
                  <option value="semi_autonomous">Semi-autonomous</option>
                  <option value="fully_autonomous">Fully autonomous</option>
                </select>
              </label>
            </div>

            <label className="form-field">
              <FieldLabel label="Tech stack" help="Comma-separated core systems used to run the agent." />
              <input
                className="ui-input"
                type="text"
                placeholder="OpenAI, Supabase"
                value={form.stack}
                onChange={(event) => updateField('stack', event.target.value)}
              />
            </label>

            <label className="form-field">
              <FieldLabel label="Tools used" help="Explain the concrete tasks or tools involved in the workflow." />
              <input
                className="ui-input"
                type="text"
                placeholder="Web research, CRM enrichment, Email copy generation"
                value={form.toolsUsed}
                onChange={(event) => updateField('toolsUsed', event.target.value)}
              />
            </label>

            <label className="form-field">
              <FieldLabel label="Integrations" help="List the buyer tools you can connect or deliver into." />
              <input
                className="ui-input"
                type="text"
                placeholder="HubSpot, Salesforce, Airtable"
                value={form.integrations}
                onChange={(event) => updateField('integrations', event.target.value)}
              />
            </label>

            <div className="form-grid">
              <label className="form-field">
                <FieldLabel label="Industries" help="Comma-separated industries this listing fits best." />
                <input
                  className="ui-input"
                  type="text"
                  placeholder="SaaS, Agencies"
                  value={form.industries}
                  onChange={(event) => updateField('industries', event.target.value)}
                />
              </label>
              <label className="form-field">
                <FieldLabel label="Best for" help="Buyer personas or teams that get the most value." />
                <input
                  className="ui-input"
                  type="text"
                  placeholder="Founders, Growth teams, SDRs"
                  value={form.bestFor}
                  onChange={(event) => updateField('bestFor', event.target.value)}
                />
              </label>
              <label className="form-field">
                <FieldLabel label="Use cases" help="Specific jobs this agent is hired to do." />
                <input
                  className="ui-input"
                  type="text"
                  placeholder="Lead research, CRM enrichment"
                  value={form.useCases}
                  onChange={(event) => updateField('useCases', event.target.value)}
                />
              </label>
              <label className="form-field">
                <FieldLabel label="Outcomes" help="What buyers should expect to improve after launch." />
                <input
                  className="ui-input"
                  type="text"
                  placeholder="Faster prospect research, Better lead prioritization"
                  value={form.outcomes}
                  onChange={(event) => updateField('outcomes', event.target.value)}
                />
              </label>
            </div>

            <label className="form-field">
              <FieldLabel label="Not for" help="Say where the agent is a bad fit so buyers self-qualify earlier." />
              <input
                className="ui-input"
                type="text"
                placeholder="Consumer ecommerce, Spam campaigns"
                value={form.notFor}
                onChange={(event) => updateField('notFor', event.target.value)}
              />
            </label>
            </div>
          </AccordionSection>

          <div className="form-section">
            <h2>Buyer inputs</h2>
            <label className="form-field">
              <FieldLabel
                label="Does your agent require buyer inputs?"
                help="Answer yes if the buyer needs to send files, text, access, or context before work can begin."
                required
              />
              <select
                className="ui-select"
                value={form.requiresBuyerInputs}
                onChange={(event) => updateField('requiresBuyerInputs', event.target.value)}
                required
              >
                <option value="">Select one</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>

            {form.requiresBuyerInputs === 'yes' ? (
              <div className="form-section">
            <div className="editor-collection">
              <div className="editor-collection-head">
                <h3>Required inputs</h3>
                <button
                  type="button"
                  className="text-button icon-button"
                  onClick={() => addArrayItem('requiredBuyerInputs', createBuyerInput('required'))}
                >
                  <Plus size={16} />
                  Add required input
                </button>
              </div>
              {form.requiredBuyerInputs.map((item) => (
                <div key={item.id} className="editor-card">
                  <div className="form-grid">
                    <label className="form-field">
                      <FieldLabel label="Input name" help="What the buyer needs to provide." />
                      <input
                        className="ui-input"
                        type="text"
                        placeholder="Target account list"
                        value={item.name}
                        required={form.requiresBuyerInputs === 'yes'}
                        onChange={(event) =>
                          updateArrayItem('requiredBuyerInputs', item.id, { name: event.target.value })
                        }
                      />
                    </label>
                    <label className="form-field">
                      <FieldLabel label="Input type" help="How the buyer will provide this input." />
                      <select
                        className="ui-select"
                        value={item.type}
                        required={form.requiresBuyerInputs === 'yes'}
                        onChange={(event) =>
                          updateArrayItem('requiredBuyerInputs', item.id, { type: event.target.value })
                        }
                      >
                        <option value="text">Text</option>
                        <option value="long_text">Long text</option>
                        <option value="file">File</option>
                        <option value="file_or_text">File or text</option>
                        <option value="integration">Integration</option>
                        <option value="url">URL</option>
                        <option value="number">Number</option>
                      </select>
                    </label>
                    <label className="form-field form-field-wide">
                      <FieldLabel label="Accepted formats" help="Comma-separated, only if relevant." />
                      <input
                        className="ui-input"
                        type="text"
                        value={item.acceptedFormats}
                        onChange={(event) =>
                          updateArrayItem('requiredBuyerInputs', item.id, { acceptedFormats: event.target.value })
                        }
                      />
                    </label>
                  </div>
                  <label className="form-field">
                    <FieldLabel label="Description" help="Explain why this input is needed." />
                    <textarea
                      className="ui-textarea"
                      rows="2"
                      placeholder="Explain why this input is needed."
                      value={item.description}
                      required={form.requiresBuyerInputs === 'yes'}
                      onChange={(event) =>
                        updateArrayItem('requiredBuyerInputs', item.id, { description: event.target.value })
                      }
                    />
                  </label>
                  {form.requiredBuyerInputs.length > 1 ? (
                    <button
                      type="button"
                      className="text-button destructive-text-button"
                      onClick={() => removeArrayItem('requiredBuyerInputs', item.id)}
                    >
                      <Trash2 size={15} />
                      Remove input
                    </button>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="editor-collection">
              <div className="editor-collection-head">
                <h3>Optional inputs</h3>
                <button
                  type="button"
                  className="text-button icon-button"
                  onClick={() => addArrayItem('optionalBuyerInputs', createBuyerInput('optional'))}
                >
                  <Plus size={16} />
                  Add optional input
                </button>
              </div>
              {form.optionalBuyerInputs.map((item) => (
                <div key={item.id} className="editor-card">
                  <div className="form-grid">
                    <label className="form-field">
                      <FieldLabel label="Input name" help="Optional but helpful buyer input." />
                      <input
                        className="ui-input"
                        type="text"
                        placeholder="Brand voice examples"
                        value={item.name}
                        onChange={(event) =>
                          updateArrayItem('optionalBuyerInputs', item.id, { name: event.target.value })
                        }
                      />
                    </label>
                    <label className="form-field">
                      <FieldLabel label="Input type" help="How the optional input is usually provided." />
                      <select
                        className="ui-select"
                        value={item.type}
                        onChange={(event) =>
                          updateArrayItem('optionalBuyerInputs', item.id, { type: event.target.value })
                        }
                      >
                        <option value="text">Text</option>
                        <option value="long_text">Long text</option>
                        <option value="file">File</option>
                        <option value="file_or_text">File or text</option>
                        <option value="integration">Integration</option>
                        <option value="url">URL</option>
                        <option value="number">Number</option>
                      </select>
                    </label>
                    <label className="form-field form-field-wide">
                      <FieldLabel label="Accepted formats" help="Comma-separated, only if relevant." />
                      <input
                        className="ui-input"
                        type="text"
                        value={item.acceptedFormats}
                        onChange={(event) =>
                          updateArrayItem('optionalBuyerInputs', item.id, { acceptedFormats: event.target.value })
                        }
                      />
                    </label>
                  </div>
                  <label className="form-field">
                    <FieldLabel label="Description" help="Explain when this input helps improve the output." />
                    <textarea
                      className="ui-textarea"
                      rows="2"
                      placeholder="Explain when this helps improve the output."
                      value={item.description}
                      onChange={(event) =>
                        updateArrayItem('optionalBuyerInputs', item.id, { description: event.target.value })
                      }
                    />
                  </label>
                  <button
                    type="button"
                    className="text-button destructive-text-button"
                    onClick={() => removeArrayItem('optionalBuyerInputs', item.id)}
                  >
                    <Trash2 size={15} />
                    Remove input
                  </button>
                </div>
              ))}
            </div>
              </div>
            ) : null}
          </div>

          <div className="form-section">
            <h2>Deliverables and workflow</h2>
            <div className="editor-collection">
              <div className="editor-collection-head">
                <h3>Deliverables</h3>
                <button
                  type="button"
                  className="text-button icon-button"
                  onClick={() => addArrayItem('deliverables', createDeliverable())}
                >
                  <Plus size={16} />
                  Add deliverable
                </button>
              </div>
              {form.deliverables.map((item) => (
                <div key={item.id} className="editor-card">
                  <div className="form-grid">
                    <label className="form-field">
                      <FieldLabel label="Deliverable name" help="What the buyer receives." required />
                      <input
                        className="ui-input"
                        type="text"
                        placeholder="Lead research report"
                        value={item.name}
                        required
                        onChange={(event) => updateArrayItem('deliverables', item.id, { name: event.target.value })}
                      />
                    </label>
                    <label className="form-field">
                      <FieldLabel label="Format" help="Choose the output format the buyer receives." required />
                      <select
                        className="ui-select"
                        value={item.format}
                        required
                        onChange={(event) =>
                          updateArrayItem('deliverables', item.id, { format: event.target.value })
                        }
                      >
                        <option value="">Select format</option>
                        {deliverableFormatOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="form-field">
                      <FieldLabel
                        label="Delivery method"
                        help="How the buyer receives the finished output."
                        required
                      />
                      <select
                        className="ui-select"
                        value={item.deliveryMethod}
                        required
                        onChange={(event) =>
                          updateArrayItem('deliverables', item.id, { deliveryMethod: event.target.value })
                        }
                      >
                        <option value="">Select delivery method</option>
                        {deliverableMethodOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <label className="form-field">
                    <FieldLabel label="Description" help="Explain the deliverable in buyer language." required />
                    <textarea
                      className="ui-textarea"
                      rows="2"
                      placeholder="Describe what the buyer receives."
                      value={item.description}
                      required
                      onChange={(event) =>
                        updateArrayItem('deliverables', item.id, { description: event.target.value })
                      }
                    />
                  </label>
                  {form.deliverables.length > 1 ? (
                    <button
                      type="button"
                      className="text-button destructive-text-button"
                      onClick={() => removeArrayItem('deliverables', item.id)}
                    >
                      <Trash2 size={15} />
                      Remove deliverable
                    </button>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="editor-collection">
              <div className="editor-collection-head">
                <h3>Workflow steps</h3>
                <button type="button" className="text-button icon-button" onClick={addWorkflowStep}>
                  <Plus size={16} />
                  Add workflow step
                </button>
              </div>
              <div className="workflow-list">
                {form.workflowSteps.map((step, index) => (
                  <div key={`workflow-${index}`} className="workflow-step-row">
                    <span className="workflow-step-number">{index + 1}</span>
                    <input
                      className="ui-input"
                      type="text"
                      value={step}
                      placeholder="Describe this step"
                      required
                      onChange={(event) => updateWorkflowStep(index, event.target.value)}
                    />
                    {form.workflowSteps.length > 1 ? (
                      <button
                        type="button"
                        className="icon-only-button"
                        aria-label="Remove workflow step"
                        onClick={() => removeWorkflowStep(index)}
                      >
                        <Trash2 size={15} />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <AccordionSection
            title="Trust, proof, and FAQs"
            description="Optional. Add proof points, examples, policies, and common questions."
          >
            <div className="form-section optional-form-section">
            <label className="form-field">
              <FieldLabel label="Featured quote" help="Short proof point or positioning line used in highlights." />
              <input
                className="ui-input"
                type="text"
                placeholder="Cut research time by 80% in the first week."
                value={form.featuredQuote}
                onChange={(event) => updateField('featuredQuote', event.target.value)}
              />
            </label>

            <div className="form-grid">
              <label className="form-field">
                <FieldLabel label="Output formats" help="Comma-separated formats you can deliver in." />
                <input
                  className="ui-input"
                  type="text"
                  placeholder="Google Sheet, CSV, PDF"
                  value={form.outputFormats}
                  onChange={(event) => updateField('outputFormats', event.target.value)}
                />
              </label>
              <label className="form-field">
                <FieldLabel label="Demo video URL" help="Optional walkthrough or Loom demo link." />
                <input
                  className="ui-input"
                  type="url"
                  placeholder="https://example.com/demo-video"
                  value={form.demoVideoUrl}
                  onChange={(event) => updateField('demoVideoUrl', event.target.value)}
                />
              </label>
            </div>

            <div className="form-grid">
              <label className="form-field">
                <FieldLabel label="Sample output available" help="Turn on if you have a shareable output example." />
                <select
                  className="ui-select"
                  value={form.sampleOutputEnabled ? 'yes' : 'no'}
                  onChange={(event) => updateField('sampleOutputEnabled', event.target.value === 'yes')}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </label>
              <label className="form-field">
                <FieldLabel label="Sample output URL" help="Public link to a sample report, sheet, doc, or preview." />
                <input
                  className="ui-input"
                  type="url"
                  placeholder="https://example.com/sample-output"
                  value={form.sampleOutputUrl}
                  onChange={(event) => updateField('sampleOutputUrl', event.target.value)}
                  disabled={!form.sampleOutputEnabled}
                />
              </label>
            </div>

            {form.sampleOutputEnabled ? (
              <label className="form-field">
                <FieldLabel label="Sample output description" help="Tell buyers what they are looking at." />
                <textarea
                  className="ui-textarea"
                  rows="2"
                  value={form.sampleOutputDescription}
                  onChange={(event) => updateField('sampleOutputDescription', event.target.value)}
                />
              </label>
            ) : null}

            <div className="form-grid">
              <label className="form-field">
                <FieldLabel label="Stores buyer data" help="Disclose whether buyer data is stored after processing." />
                <select
                  className="ui-select"
                  value={form.storesBuyerData ? 'yes' : 'no'}
                  onChange={(event) => updateField('storesBuyerData', event.target.value === 'yes')}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
              <label className="form-field">
                <FieldLabel label="Retention days" help="How long stored buyer data is retained." />
                <input
                  className="ui-input"
                  type="number"
                  min="0"
                  value={form.retentionDays}
                  onChange={(event) => updateField('retentionDays', event.target.value)}
                />
              </label>
              <label className="form-field">
                <FieldLabel label="Uses buyer data for training" help="Should almost always be No unless clearly disclosed." />
                <select
                  className="ui-select"
                  value={form.usesBuyerDataForTraining ? 'yes' : 'no'}
                  onChange={(event) =>
                    updateField('usesBuyerDataForTraining', event.target.value === 'yes')
                  }
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </label>
              <label className="form-field">
                <FieldLabel label="Requires credentials" help="Does setup require buyer account access or credentials?" />
                <select
                  className="ui-select"
                  value={form.requiresCredentials ? 'yes' : 'no'}
                  onChange={(event) => updateField('requiresCredentials', event.target.value === 'yes')}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </label>
              <label className="form-field">
                <FieldLabel label="Sensitive data allowed" help="Use only if the workflow is safe for regulated or sensitive data." />
                <select
                  className="ui-select"
                  value={form.sensitiveDataAllowed ? 'yes' : 'no'}
                  onChange={(event) => updateField('sensitiveDataAllowed', event.target.value === 'yes')}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </label>
              <label className="form-field">
                <FieldLabel label="Third-party tools used" help="Disclose if output passes through other services or integrations." />
                <select
                  className="ui-select"
                  value={form.thirdPartyToolsUsed ? 'yes' : 'no'}
                  onChange={(event) => updateField('thirdPartyToolsUsed', event.target.value === 'yes')}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
              <label className="form-field">
                <FieldLabel label="Requires buyer review" help="Recommended for any workflow that creates external messaging or actions." />
                <select
                  className="ui-select"
                  value={form.requiresBuyerReview ? 'yes' : 'no'}
                  onChange={(event) => updateField('requiresBuyerReview', event.target.value === 'yes')}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
            </div>

            <label className="form-field">
              <FieldLabel label="Prohibited uses" help="Comma-separated list of unsafe or disallowed uses." />
              <input
                className="ui-input"
                type="text"
                placeholder="Spam, Phishing, Impersonation"
                value={form.prohibitedUses}
                onChange={(event) => updateField('prohibitedUses', event.target.value)}
              />
            </label>

            <label className="form-field">
              <FieldLabel label="Limitations" help="One limitation per line so buyers understand guardrails and constraints." />
              <textarea
                className="ui-textarea"
                rows="4"
                placeholder="Does not send emails on behalf of the buyer."
                value={form.limitations}
                onChange={(event) => updateField('limitations', event.target.value)}
              />
            </label>

            <div className="editor-collection">
              <div className="editor-collection-head">
                <h3>FAQs</h3>
                <button
                  type="button"
                  className="text-button icon-button"
                  onClick={() => addArrayItem('faqs', createFaq())}
                >
                  <Plus size={16} />
                  Add FAQ
                </button>
              </div>
              {form.faqs.map((faq) => (
                <div key={faq.id} className="editor-card">
                  <label className="form-field">
                    <FieldLabel label="Question" help="A common buyer question that should be answered before purchase." />
                    <input
                      className="ui-input"
                      type="text"
                      placeholder="What do you need from the buyer to launch?"
                      value={faq.question}
                      onChange={(event) => updateArrayItem('faqs', faq.id, { question: event.target.value })}
                    />
                  </label>
                  <label className="form-field">
                    <FieldLabel label="Answer" help="A direct answer that reduces back-and-forth with buyers." />
                    <textarea
                      className="ui-textarea"
                      rows="3"
                      placeholder="A target account list, offer details, and any brand guidance."
                      value={faq.answer}
                      onChange={(event) => updateArrayItem('faqs', faq.id, { answer: event.target.value })}
                    />
                  </label>
                  {form.faqs.length > 1 ? (
                    <button
                      type="button"
                      className="text-button destructive-text-button"
                      onClick={() => removeArrayItem('faqs', faq.id)}
                    >
                      <Trash2 size={15} />
                      Remove FAQ
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
            </div>
          </AccordionSection>

          <button type="submit" className="primary-button ui-button full-width" disabled={listingSubmitting}>
            {publishButtonLabel}
          </button>
          {status ? <p className="status-note">{status}</p> : null}
        </form>
      </div>
    </section>
  );
}
